import {
  COMPETITION_ROLE_VALUES,
  COMPETITION_STATUS_VALUES,
  DEFAULT_COMPETITION_LOCALE,
  DEFAULT_COMPETITION_TIMEZONE,
  PERMISSION_VALUES,
} from '@napaicp/shared'
import { z } from 'zod'
import { competitionLogoDtoSchema } from './competition-logo'
import { publicFaqEntrySchema } from './competition-settings'
import {
  ianaTimeZoneSchema,
  localeSchema,
  nullableHttpsUrlSchema,
  publicIdSchema,
  slugSchema,
  utcTimestampSchema,
} from './primitives'
import { submissionStatusSchema } from './submission'

export const competitionStatusSchema = z.enum(COMPETITION_STATUS_VALUES)

// ---------------------------------------------------------------------------
// Cross-field rules, shared by the stored record and by the write requests so
// that a value rejected on the way in can never be produced on the way out.
// ---------------------------------------------------------------------------

interface SchedulePairs {
  registrationStartAt?: string | null
  registrationEndAt?: string | null
  submissionStartAt?: string | null
  submissionEndAt?: string | null
  judgingStartAt?: string | null
  judgingEndAt?: string | null
}

const ORDERED_WINDOWS = [
  ['registrationStartAt', 'registrationEndAt'],
  ['submissionStartAt', 'submissionEndAt'],
  ['judgingStartAt', 'judgingEndAt'],
] as const

function addScheduleIssues(value: SchedulePairs, ctx: z.RefinementCtx): void {
  for (const [startKey, endKey] of ORDERED_WINDOWS) {
    const start = value[startKey]
    const end = value[endKey]
    if (start != null && end != null && Date.parse(end) <= Date.parse(start)) {
      ctx.addIssue({
        code: 'custom',
        path: [endKey],
        message: `${endKey} must be after ${startKey}.`,
      })
    }
  }
}

interface RulePairs {
  teamMinMembers?: number
  teamMaxMembers?: number
  allowTeacher?: boolean
  requireTeacher?: boolean
}

function addRuleIssues(value: RulePairs, ctx: z.RefinementCtx): void {
  const { teamMinMembers, teamMaxMembers, allowTeacher, requireTeacher } = value

  if (teamMinMembers != null && teamMaxMembers != null && teamMaxMembers < teamMinMembers) {
    ctx.addIssue({
      code: 'custom',
      path: ['teamMaxMembers'],
      message: 'teamMaxMembers must be greater than or equal to teamMinMembers.',
    })
  }

  if (requireTeacher === true && allowTeacher === false) {
    ctx.addIssue({
      code: 'custom',
      path: ['requireTeacher'],
      message: 'A teacher cannot be required while teachers are not allowed.',
    })
  }
}

/**
 * Competition timing. Every value is a UTC instant; `timezone` only decides how
 * it is rendered. Ordering is validated as a whole so a deadline cannot be
 * saved before the window that feeds it.
 */
export const competitionScheduleSchema = z
  .object({
    registrationStartAt: utcTimestampSchema.nullable(),
    registrationEndAt: utcTimestampSchema.nullable(),
    submissionStartAt: utcTimestampSchema.nullable(),
    submissionEndAt: utcTimestampSchema.nullable(),
    judgingStartAt: utcTimestampSchema.nullable(),
    judgingEndAt: utcTimestampSchema.nullable(),
    resultPublishAt: utcTimestampSchema.nullable(),
  })
  .superRefine(addScheduleIssues)

export type CompetitionSchedule = z.infer<typeof competitionScheduleSchema>

/**
 * Team size and participation rules.
 */
export const competitionRulesSchema = z
  .object({
    teamMinMembers: z.int().min(1).max(100),
    teamMaxMembers: z.int().min(1).max(100),
    allowTeacher: z.boolean(),
    requireTeacher: z.boolean(),
    allowMultipleSubmissions: z.boolean(),
    anonymousJudging: z.boolean(),
  })
  .superRefine(addRuleIssues)

export type CompetitionRules = z.infer<typeof competitionRulesSchema>

const competitionCoreShape = {
  id: publicIdSchema,
  name: z.string().trim().min(1).max(200),
  slug: slugSchema,
  /** Structured external competition website — deliberately independent of `slug`. See §6.3 of the SDD. */
  officialWebsiteUrl: nullableHttpsUrlSchema,
  /** References `competition_logo_assets.id`, never the image binary — see `competition-logo.ts`. */
  logoFileId: publicIdSchema.nullable(),
  shortDescription: z.string().max(500).nullable(),
  description: z.string().max(20_000).nullable(),
  year: z.int().min(2000).max(2999),
  /** Last computed lifecycle status. */
  status: competitionStatusSchema,
  /** Administrator pin. When set it wins over the computed status. */
  statusOverride: competitionStatusSchema.nullable(),
  timezone: ianaTimeZoneSchema,
  locale: localeSchema,
  createdByUserId: publicIdSchema,
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
  archivedAt: utcTimestampSchema.nullable(),
}

export const competitionSchema = z
  .object(competitionCoreShape)
  .and(competitionScheduleSchema)
  .and(competitionRulesSchema)

export type Competition = z.infer<typeof competitionSchema>

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * What a competition looks like in a list. The administrator's status pin is
 * not exposed here; `status` is the effective status, which is all a caller
 * needs in order to decide what to render.
 */
export const competitionSummaryDtoSchema = z.object({
  id: publicIdSchema,
  name: z.string(),
  slug: slugSchema,
  officialWebsiteUrl: nullableHttpsUrlSchema,
  /** `null` when the competition has no logo set. Never `logoFileId` raw — see `competition-logo.ts`. */
  logo: competitionLogoDtoSchema.nullable(),
  shortDescription: z.string().nullable(),
  year: z.int(),
  status: competitionStatusSchema,
  timezone: ianaTimeZoneSchema,
  locale: localeSchema,
  /** Roles the calling account holds here. Empty for a super admin with no explicit grant. */
  roles: z.array(z.enum(COMPETITION_ROLE_VALUES)),
})

export type CompetitionSummaryDto = z.infer<typeof competitionSummaryDtoSchema>

/**
 * The full record, for callers holding `competition.read` in this competition.
 *
 * `status` is the effective status the platform acts on. `computedStatus` and
 * `statusOverride` are reported separately so an administrator can see both
 * what the schedule implies and whether a pin is overriding it — collapsing
 * them into one field is how a deliberate override gets silently reverted.
 */
export const competitionDetailDtoSchema = z
  .object({
    id: publicIdSchema,
    name: z.string(),
    slug: slugSchema,
    officialWebsiteUrl: nullableHttpsUrlSchema,
    logo: competitionLogoDtoSchema.nullable(),
    shortDescription: z.string().nullable(),
    description: z.string().nullable(),
    year: z.int(),
    status: competitionStatusSchema,
    computedStatus: competitionStatusSchema,
    statusOverride: competitionStatusSchema.nullable(),
    timezone: ianaTimeZoneSchema,
    locale: localeSchema,
    createdAt: utcTimestampSchema,
    updatedAt: utcTimestampSchema,
    archivedAt: utcTimestampSchema.nullable(),
    roles: z.array(z.enum(COMPETITION_ROLE_VALUES)),
    /** What the caller may do here. Advisory for the UI; the server re-checks. */
    permissions: z.array(z.enum(PERMISSION_VALUES)),
  })
  .and(competitionScheduleSchema)
  .and(competitionRulesSchema)

export type CompetitionDetailDto = z.infer<typeof competitionDetailDtoSchema>

// ---------------------------------------------------------------------------
// Write requests
// ---------------------------------------------------------------------------

/** Step 1 of the competition builder: identity, schedule and eligibility. */
export const createCompetitionRequestSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(200),
    slug: slugSchema,
    officialWebsiteUrl: nullableHttpsUrlSchema.optional().default(null),
    shortDescription: z.string().max(500).nullable().default(null),
    description: z.string().max(20_000).nullable().default(null),
    year: z.int().min(2000).max(2999),
    timezone: ianaTimeZoneSchema.default(DEFAULT_COMPETITION_TIMEZONE),
    locale: localeSchema.default(DEFAULT_COMPETITION_LOCALE),
    registrationStartAt: utcTimestampSchema.nullable().default(null),
    registrationEndAt: utcTimestampSchema.nullable().default(null),
    submissionStartAt: utcTimestampSchema.nullable().default(null),
    submissionEndAt: utcTimestampSchema.nullable().default(null),
    judgingStartAt: utcTimestampSchema.nullable().default(null),
    judgingEndAt: utcTimestampSchema.nullable().default(null),
    resultPublishAt: utcTimestampSchema.nullable().default(null),
    teamMinMembers: z.int().min(1).max(100).default(1),
    teamMaxMembers: z.int().min(1).max(100).default(5),
    allowTeacher: z.boolean().default(true),
    requireTeacher: z.boolean().default(false),
    allowMultipleSubmissions: z.boolean().default(false),
    anonymousJudging: z.boolean().default(false),
  })
  .superRefine((value, ctx) => {
    addScheduleIssues(value, ctx)
    addRuleIssues(value, ctx)
  })

export type CreateCompetitionRequest = z.infer<typeof createCompetitionRequestSchema>

/**
 * A partial update. Cross-field rules are re-checked in the handler against the
 * merged record, because a patch that moves only `submissionEndAt` can still
 * invert a window whose start it never mentions.
 */
export const updateCompetitionRequestSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(200).optional(),
    slug: slugSchema.optional(),
    officialWebsiteUrl: nullableHttpsUrlSchema.optional(),
    /** Must reference a `ready` `competition_logo_assets` row owned by this competition, or `null` to remove the logo — validated server-side in the handler, not by this shape alone. */
    logoFileId: publicIdSchema.nullable().optional(),
    shortDescription: z.string().max(500).nullable().optional(),
    description: z.string().max(20_000).nullable().optional(),
    year: z.int().min(2000).max(2999).optional(),
    timezone: ianaTimeZoneSchema.optional(),
    locale: localeSchema.optional(),
    registrationStartAt: utcTimestampSchema.nullable().optional(),
    registrationEndAt: utcTimestampSchema.nullable().optional(),
    submissionStartAt: utcTimestampSchema.nullable().optional(),
    submissionEndAt: utcTimestampSchema.nullable().optional(),
    judgingStartAt: utcTimestampSchema.nullable().optional(),
    judgingEndAt: utcTimestampSchema.nullable().optional(),
    resultPublishAt: utcTimestampSchema.nullable().optional(),
    teamMinMembers: z.int().min(1).max(100).optional(),
    teamMaxMembers: z.int().min(1).max(100).optional(),
    allowTeacher: z.boolean().optional(),
    requireTeacher: z.boolean().optional(),
    allowMultipleSubmissions: z.boolean().optional(),
    anonymousJudging: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
  })

export type UpdateCompetitionRequest = z.infer<typeof updateCompetitionRequestSchema>

/** Re-runs the cross-field rules over an already-merged competition record. */
export const mergedCompetitionRulesSchema = z
  .object({
    registrationStartAt: utcTimestampSchema.nullable(),
    registrationEndAt: utcTimestampSchema.nullable(),
    submissionStartAt: utcTimestampSchema.nullable(),
    submissionEndAt: utcTimestampSchema.nullable(),
    judgingStartAt: utcTimestampSchema.nullable(),
    judgingEndAt: utcTimestampSchema.nullable(),
    teamMinMembers: z.int(),
    teamMaxMembers: z.int(),
    allowTeacher: z.boolean(),
    requireTeacher: z.boolean(),
  })
  .superRefine((value, ctx) => {
    addScheduleIssues(value, ctx)
    addRuleIssues(value, ctx)
  })

/**
 * Pins or releases the lifecycle status. `statusOverride: null` hands control
 * back to the schedule. The reason is mandatory: overriding the timetable of a
 * live competition is exactly the kind of decision that has to be explainable
 * afterwards, and it is what lands in the audit log.
 */
export const updateCompetitionStatusRequestSchema = z.strictObject({
  statusOverride: competitionStatusSchema.nullable(),
  reason: z.string().trim().min(3).max(500),
})

export type UpdateCompetitionStatusRequest = z.infer<typeof updateCompetitionStatusRequestSchema>

// ---------------------------------------------------------------------------
// Public Discovery (PR-125/API-065) — search/filter/sort/pagination over
// `GET /public/competitions`. Everything here stays derivable from existing
// columns; no `visibility`/`organizerName`/popularity field exists yet (see
// `docs/sdd/09-delivery-change-control.md`'s v3.13 entry for why those
// stayed out of scope).
// ---------------------------------------------------------------------------

/** `individual` iff `teamMaxMembers === 1`; there is no separate stored field for this. */
export const PARTICIPATION_TYPE_VALUES = ['individual', 'team'] as const
export const participationTypeSchema = z.enum(PARTICIPATION_TYPE_VALUES)
export type ParticipationType = z.infer<typeof participationTypeSchema>

/** A public-facing subset of `competitionStatusSchema` — `draft` is never publicly reachable. */
export const publicCompetitionStatusFilterSchema = z.enum([
  'published',
  'registration_open',
  'submission_open',
  'judging',
  'completed',
])
export type PublicCompetitionStatusFilter = z.infer<typeof publicCompetitionStatusFilterSchema>

export const publicCompetitionSortSchema = z.enum(['newest', 'deadline']).default('newest')
export type PublicCompetitionSort = z.infer<typeof publicCompetitionSortSchema>

export const publicCompetitionListQuerySchema = z.object({
  /** Case-insensitive substring match against `name`/`shortDescription`. */
  q: z.string().trim().max(200).optional(),
  status: publicCompetitionStatusFilterSchema.optional(),
  participationType: participationTypeSchema.optional(),
  /**
   * `registration_open`/`submission_open` whose relevant deadline is within
   * 7 days — MVP threshold, not admin-configurable (see FR-01's "近 7 日"
   * precedent for popularity; reused here for consistency, not derived from
   * it).
   */
  closingSoon: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => value === 'true'),
  /** PR-130: platform Discovery Category slug — distinct from `categories` below (competition's own tracks). */
  category: slugSchema.optional(),
  sort: publicCompetitionSortSchema,
  limit: z.coerce.number().int().min(1).max(50).default(50),
  offset: z.coerce.number().int().min(0).default(0),
})
export type PublicCompetitionListQuery = z.infer<typeof publicCompetitionListQuerySchema>

export const publicCompetitionSummaryDtoSchema = z.object({
  name: z.string(),
  slug: slugSchema,
  officialWebsiteUrl: nullableHttpsUrlSchema,
  logo: competitionLogoDtoSchema.nullable(),
  shortDescription: z.string().nullable(),
  description: z.string().nullable(),
  timezone: ianaTimeZoneSchema,
  locale: localeSchema,
  status: publicCompetitionStatusFilterSchema,
  participationType: participationTypeSchema,
  /** PR-160: exposes the existing `competitions` team-size/advisor rules so the public page can render "參賽重點" without inventing new state. */
  teamMinMembers: z.int(),
  teamMaxMembers: z.int(),
  allowTeacher: z.boolean(),
  requireTeacher: z.boolean(),
  registrationStartAt: utcTimestampSchema.nullable(),
  registrationEndAt: utcTimestampSchema.nullable(),
  submissionStartAt: utcTimestampSchema.nullable(),
  submissionEndAt: utcTimestampSchema.nullable(),
  resultPublishAt: utcTimestampSchema.nullable(),
  registrationInstructions: z.string().nullable(),
  /** Public Competition Page v1 structured fields Spec Delta (2026-08-26): admin free-text, `null` hides the section. */
  eligibilityText: z.string().nullable(),
  submissionRequirementsText: z.string().nullable(),
  judgingCriteriaText: z.string().nullable(),
  prizesText: z.string().nullable(),
  organizerContactText: z.string().nullable(),
  faq: z.array(publicFaqEntrySchema),
  categories: z.array(z.object({ name: z.string(), description: z.string().nullable() })),
  /** PR-130: platform Discovery Category names this competition opted into — may be empty. */
  discoveryCategories: z.array(z.object({ name: z.string(), slug: slugSchema })),
  announcements: z.array(
    z.object({
      id: publicIdSchema,
      title: z.string(),
      body: z.string(),
      publishedAt: utcTimestampSchema,
    }),
  ),
})
export type PublicCompetitionSummaryDto = z.infer<typeof publicCompetitionSummaryDtoSchema>

/**
 * PR-133/API-069: a signed-in visitor's own participation state for the
 * competition, computed server-side from the same repos the authenticated
 * student pages already use. `null` for anonymous visitors and for
 * signed-in visitors who hold no role in this competition — both are "you
 * have nothing here yet", indistinguishable by design so this never becomes
 * an account-enumeration signal.
 */
export const publicCompetitionMyStatusDtoSchema = z.object({
  competitionId: publicIdSchema,
  role: z.enum(COMPETITION_ROLE_VALUES),
  /** `null` iff the competition has no published `registration_form`. */
  registrationFormCompleted: z.boolean().nullable(),
  /** `null` unless `role === 'student'`. */
  hasTeam: z.boolean().nullable(),
  /** `null` unless `role === 'student'` and a team submission exists. */
  submissionStatus: submissionStatusSchema.nullable(),
  /**
   * PR-258/BR-191 (Student Journey Completion Wave SJ-3): `true` when the
   * latest submission has a non-null `submittedAt` — i.e. it was formally
   * submitted at least once before, even if `submissionStatus` now reads
   * `draft`/`ready` again (staff reopened it via `/unlock` or
   * `/return-for-correction`, which move status back but never clear
   * `submittedAt`). `false` when there is no submission yet.
   */
  wasSubmittedBefore: z.boolean(),
  resultPublished: z.boolean(),
  /**
   * PR-264 (Wave SJ-5 API delta): `true` when the latest submission belongs
   * to a round with `roundOrder > 1` (the final round; a competition has at
   * most a preliminary + one final round) — `false` when there is no
   * submission yet or it belongs to the preliminary round. Lets
   * `participant-cta.ts` pass real round context into
   * `deriveStudentJourneyStage` instead of a hardcoded `false`.
   */
  isFinalRound: z.boolean(),
})
export type PublicCompetitionMyStatusDto = z.infer<typeof publicCompetitionMyStatusDtoSchema>
